/**
 * IndexedDB cache for every response the Ads & Sales Reconciliation screen reads.
 *
 * Nine endpoints back this screen, and only one of them (`/ads-dashboard`) has a
 * validator on the wire — so a revisit re-downloaded all nine in full. This
 * stores each response so a repeat visit paints from disk, and so the 08:00
 * Central boundary can be honoured without throwing the data away.
 *
 * Follows the pattern already established by `features/spi/api/spiDatasetCache.ts`
 * and `features/asana/api/poIndexedDbCache.ts`: IndexedDB through the shared
 * `lib/indexedDb.ts` wrapper, a version stamp that discards a stale-SHAPE
 * record, and every operation best-effort — a cache failure degrades to a normal
 * network fetch and never surfaces as an error.
 *
 * ## Three states, not two
 *
 * A read returns `fresh` | `must-revalidate` | `miss`, and the middle one is the
 * whole point. An entry written before the last 08:00 America/Chicago boundary is
 * NOT deleted: the caller asks the server whether it changed, and a `304` keeps
 * the stored copy with nothing re-downloaded. Deleting it would satisfy the same
 * correctness rule while discarding ~250 KB the server was about to tell us was
 * still current.
 *
 * ## Why localStorage was not an option
 *
 * The dashboard payload alone is a few hundred kilobytes and grows with the
 * window; localStorage is ~5 MB for the whole origin, shared with the Vue ERP,
 * synchronous, and already carries the permission snapshot.
 *
 * ## Lifetime
 *
 * Wiped on logout with everything else — `utils/clearAppCaches.ts` deletes every
 * IndexedDB database on the origin, and `clearMarketingCache()` is registered
 * there as well for browsers where `indexedDB.databases()` is unavailable. A
 * login by a DIFFERENT user also clears the store, because a key carries the user
 * id it was written for (see `clearIfForeignUser`).
 */
import { clearStore, del, get, getAllKeys, put, type OpenDbOptions } from '../../../lib/indexedDb';
import { serverNow } from '../../../lib/serverClock';
import { MARKETING_CACHE_MAX_AGE_MS, MARKETING_CACHE_MAX_ENTRIES } from '../../../constants/timing';
import { logger } from '../../../utils/logger';
import { isPastBoundary } from '../lib/refreshBoundary';

const DB_OPTIONS: OpenDbOptions = {
  database: 'MarketingCache',
  version: 1,
  stores: [{ name: 'responses', keyPath: 'id' }],
};

const STORE_NAME = 'responses';

/**
 * Bumped by hand whenever the SHAPE of anything stored here changes.
 *
 * This is not bookkeeping. `SalesRow` went from an 8-tuple to a 7-tuple in
 * v0.4.0 and `asins` was removed from `DashPayload`; a cached 8-tuple read by
 * 7-tuple code puts `orderedUnits` where `returns` belongs and every KPI on the
 * screen renders confidently wrong. A version mismatch is a hard miss, so the
 * next fetch overwrites the record with a current-shape one.
 *
 * Bump this when: a wire type in `types/` changes, a mapper in `api/` changes
 * what it produces, or an endpoint's parameters change meaning.
 */
export const MARKETING_CACHE_SCHEMA_VERSION = 2;

/**
 * App build version, folded into the stamp below.
 *
 * Set `REACT_APP_VERSION` in the build (git SHA or CI build number) so every
 * deploy invalidates the cache automatically; it defaults to `'dev'`, exactly as
 * `spiDatasetCache.ts` does. **If the build does not set it, this half of the
 * stamp is inert** and `MARKETING_CACHE_SCHEMA_VERSION` above is the only guard —
 * which is why that constant must be bumped by hand on any shape change rather
 * than relied on to happen automatically.
 */
const APP_VERSION = process.env.REACT_APP_VERSION ?? 'dev';

/** The stamp every record carries; a read whose stamp differs is a miss. */
const CACHE_VERSION = `${MARKETING_CACHE_SCHEMA_VERSION}|${APP_VERSION}`;

/**
 * Separates the segments of a record key: `kind|userId|params`.
 *
 * A pipe cannot appear in a `kind` (they are literals declared in
 * `cacheKinds.ts`) or in a user id (a UUID). It CAN appear inside the params
 * segment, which is why every parse below reads the first two separators only
 * and treats the remainder as opaque.
 */
const KEY_SEPARATOR = '|';

/** What a cache read can tell the caller. */
export type CacheReadState = 'fresh' | 'must-revalidate' | 'miss';

/** A stored entry, as handed back to a caller. */
export interface CacheEntry<T> {
  readonly payload: T;
  /** The `ETag` the response carried, for replay as `If-None-Match`. Null when it had none. */
  readonly etag: string | null;
  /**
   * The `from` bound this entry was actually fetched with.
   *
   * Not necessarily the `from` the caller asked for: a WIDER entry legitimately
   * serves a narrower request (see `readEntry`), and a revalidation must use the
   * window the entry itself describes or the server answers about a different
   * resource and the ETag can never match.
   */
  readonly windowFrom: string | null;
  /** The newest day on the payload's own axis, when it has one. */
  readonly axisEnd: string | null;
  readonly cachedAt: number;
}

export interface CacheRead<T> {
  readonly state: CacheReadState;
  /** Present for `fresh` and `must-revalidate`; absent for `miss`. */
  readonly entry?: CacheEntry<T>;
}

/** The record as it sits in IndexedDB. */
interface StoredRecord {
  id: string;
  kind: string;
  userId: string;
  version: string;
  cachedAt: number;
  etag: string | null;
  windowFrom: string | null;
  axisEnd: string | null;
  payload: unknown;
}

/** Anything that can appear in a request's parameter set. */
export type CacheParams = Readonly<Record<string, string | number | boolean | null | undefined>>;

/**
 * Serialise params to a stable string.
 *
 * Keys are SORTED so the caller's argument order can never split one logical
 * request across two entries, and `null`/`undefined` are dropped so "parameter
 * omitted" and "parameter explicitly empty" agree — which matches how the API
 * modules build their query objects (`...(from ? { from } : {})`).
 */
export function stableParamsKey(params: CacheParams): string {
  const entries = Object.entries(params)
    .filter(([, value]) => value !== null && value !== undefined)
    .sort(([left], [right]) => (left < right ? -1 : 1))
    .map(([key, value]) => `${key}=${String(value)}`);
  return entries.join('&');
}

/** The key a record is stored under. */
export function cacheEntryId(kind: string, userId: string, params: CacheParams): string {
  return `${kind}${KEY_SEPARATOR}${userId}${KEY_SEPARATOR}${stableParamsKey(params)}`;
}

/** Split a key back into its three segments, or null when it is not one of ours. */
function parseKey(key: IDBValidKey): { kind: string; userId: string; params: string } | null {
  if (typeof key !== 'string') return null;

  const firstSeparator = key.indexOf(KEY_SEPARATOR);
  const secondSeparator = key.indexOf(KEY_SEPARATOR, firstSeparator + 1);
  if (firstSeparator < 0 || secondSeparator < 0) return null;

  return {
    kind: key.slice(0, firstSeparator),
    userId: key.slice(firstSeparator + 1, secondSeparator),
    params: key.slice(secondSeparator + 1),
  };
}

/** Whether a record is unusable regardless of the boundary — wrong shape, wrong user, or ancient. */
function isUnusable(record: StoredRecord | undefined, userId: string, nowMs: number): boolean {
  if (!record) return true;
  if (record.version !== CACHE_VERSION) return true;
  if (record.userId !== userId) return true;
  return nowMs - record.cachedAt > MARKETING_CACHE_MAX_AGE_MS;
}

function toEntry<T>(record: StoredRecord): CacheEntry<T> {
  return {
    payload: record.payload as T,
    etag: record.etag,
    windowFrom: record.windowFrom,
    axisEnd: record.axisEnd,
    cachedAt: record.cachedAt,
  };
}

/**
 * Read the entry for exactly these params.
 *
 * A record that is unusable is DELETED and reported as a miss. A record that is
 * merely older than the last boundary is KEPT and reported as
 * `must-revalidate` — see the note at the top of this file.
 */
export async function readEntry<T>(
  kind: string,
  userId: string,
  params: CacheParams,
): Promise<CacheRead<T>> {
  const id = cacheEntryId(kind, userId, params);
  const record = await get<StoredRecord>(DB_OPTIONS, STORE_NAME, id);
  const now = serverNow();

  if (isUnusable(record, userId, now)) {
    if (record) await del(DB_OPTIONS, STORE_NAME, id);
    return { state: 'miss' };
  }

  // `isUnusable` has already rejected undefined; the cast keeps TS 4.9 happy
  // without widening the helper's signature.
  const usable = record as StoredRecord;

  return {
    state: isPastBoundary(usable.cachedAt, now) ? 'must-revalidate' : 'fresh',
    entry: toEntry<T>(usable),
  };
}

/**
 * Find an entry for the same `kind` whose window reaches back at least as far as
 * `requestedFrom` — a SUPERSET of what is being asked for.
 *
 * This is what makes partial reuse free on `/ads-dashboard`. Every range preset
 * on this screen counts POSITIONS from the end of `payload.days`
 * (`lib/aggregate.ts:rangeDays`), and the service always ends that axis at the
 * newest ingested day because the screen never sends `to`. So the last thirty
 * entries of a 400-day axis are the same thirty days as the last thirty of a
 * 30-day axis: a wider payload serves a narrower request unchanged, with no
 * re-indexing of the positional fact rows.
 *
 * Keys are scanned rather than records, so this costs no payload
 * deserialisation. The widest match wins; ties cannot occur because the key is
 * unique per `from`.
 */
async function findSupersetKey(
  kind: string,
  userId: string,
  otherParams: CacheParams,
  requestedFrom: string,
): Promise<string | null> {
  const keys = await getAllKeys(DB_OPTIONS, STORE_NAME);
  // Everything except `from`, so a candidate must match on account and the rest.
  const prefix = `${kind}${KEY_SEPARATOR}${userId}${KEY_SEPARATOR}`;

  let widestKey: string | null = null;
  let widestFrom: string | null = null;

  for (const key of keys) {
    if (typeof key !== 'string' || !key.startsWith(prefix)) continue;

    const parsed = parseKey(key);
    if (!parsed) continue;

    const candidateFrom = readFromParam(parsed.params);
    // No `from` in the key means it is not a windowed entry of this kind.
    if (!candidateFrom) continue;

    // The rest of the params must be identical, or this is a different question
    // (another account, another sort) that happens to share a kind.
    if (key !== cacheEntryId(kind, userId, { ...otherParams, from: candidateFrom })) continue;

    // Only a window reaching FURTHER back can serve this request.
    if (candidateFrom > requestedFrom) continue;

    if (widestFrom === null || candidateFrom < widestFrom) {
      widestFrom = candidateFrom;
      widestKey = key;
    }
  }

  return widestKey;
}

/** Pull the `from=` value out of a serialised params segment. */
function readFromParam(params: string): string | null {
  for (const pair of params.split('&')) {
    if (pair.startsWith('from=')) return pair.slice('from='.length);
  }
  return null;
}

/**
 * Read the entry for these params, falling back to a wider window that covers
 * them.
 *
 * The returned entry reports its OWN `windowFrom`, which the caller must use for
 * any revalidation request — revalidating a wide entry against a narrow URL
 * would compare ETags across two different resources, always miss, and replace
 * the wide payload with a narrow one.
 */
export async function readWindowedEntry<T>(
  kind: string,
  userId: string,
  params: CacheParams & { from: string },
): Promise<CacheRead<T>> {
  const exact = await readEntry<T>(kind, userId, params);
  if (exact.state !== 'miss') return exact;

  const { from, ...otherParams } = params;
  const supersetKey = await findSupersetKey(kind, userId, otherParams, from);
  if (!supersetKey) return { state: 'miss' };

  const record = await get<StoredRecord>(DB_OPTIONS, STORE_NAME, supersetKey);
  const now = serverNow();

  if (isUnusable(record, userId, now)) {
    if (record) await del(DB_OPTIONS, STORE_NAME, supersetKey);
    return { state: 'miss' };
  }

  const usable = record as StoredRecord;
  return {
    state: isPastBoundary(usable.cachedAt, now) ? 'must-revalidate' : 'fresh',
    entry: toEntry<T>(usable),
  };
}

export interface WriteEntryOptions {
  readonly kind: string;
  readonly userId: string;
  readonly params: CacheParams;
  readonly payload: unknown;
  readonly etag?: string | null;
  readonly windowFrom?: string | null;
  readonly axisEnd?: string | null;
}

/**
 * Store a response.
 *
 * Best-effort throughout: a quota error, a blocked upgrade or a browser without
 * IndexedDB all leave the screen working off the network, which is the
 * behaviour before this cache existed.
 */
export async function writeEntry(options: WriteEntryOptions): Promise<void> {
  const record: StoredRecord = {
    id: cacheEntryId(options.kind, options.userId, options.params),
    kind: options.kind,
    userId: options.userId,
    version: CACHE_VERSION,
    cachedAt: serverNow(),
    etag: options.etag ?? null,
    windowFrom: options.windowFrom ?? null,
    axisEnd: options.axisEnd ?? null,
    payload: options.payload,
  };

  const written = await put(DB_OPTIONS, STORE_NAME, record);
  if (!written) return;

  await enforceEntryCap();
}

/**
 * Move an entry past the boundary without re-downloading it, after a `304`.
 *
 * Re-stamping is what stops one 304 turning into a conditional request on every
 * subsequent read for the rest of the day.
 */
export async function touchEntry(
  kind: string,
  userId: string,
  params: CacheParams,
): Promise<void> {
  const id = cacheEntryId(kind, userId, params);
  const record = await get<StoredRecord>(DB_OPTIONS, STORE_NAME, id);
  if (!record) return;

  await put(DB_OPTIONS, STORE_NAME, { ...record, cachedAt: serverNow() });
}

/**
 * Hold the store to a bounded size.
 *
 * **Over the cap, the whole store is cleared** rather than the oldest entries
 * being evicted individually. Ranking by age would mean reading every record —
 * and therefore every payload — to find out how old it is, which is exactly the
 * work a cache exists to avoid. The cap is set high enough
 * (`MARKETING_CACHE_MAX_ENTRIES`) that an ordinary session never reaches it, so
 * the cost of the blunt instrument is one cold reload for a session that has
 * paged through an unusual number of grid permutations.
 */
async function enforceEntryCap(): Promise<void> {
  const keys = await getAllKeys(DB_OPTIONS, STORE_NAME);
  if (keys.length <= MARKETING_CACHE_MAX_ENTRIES) return;

  logger.warn('[amazonAds] Marketing cache exceeded its entry cap; clearing it.');
  await clearStore(DB_OPTIONS, STORE_NAME);
}

/**
 * Clear the store when it holds anything written for a DIFFERENT user.
 *
 * A logout already wipes every IndexedDB database on the origin
 * (`utils/clearAppCaches.ts`), so the ordinary path never needs this. It covers
 * the session that began WITHOUT a clean logout — a crashed tab, a cross-origin
 * handoff from the Vue ERP, or a second person signing in on the same browser —
 * where a previous user's records could otherwise still be sitting there.
 *
 * Keys carry the user id, so this decides by key inspection alone and never
 * deserialises a payload.
 *
 * @returns whether anything was cleared
 */
export async function clearIfForeignUser(userId: string): Promise<boolean> {
  const keys = await getAllKeys(DB_OPTIONS, STORE_NAME);

  const foreign = keys.some((key) => {
    const parsed = parseKey(key);
    return parsed !== null && parsed.userId !== userId;
  });

  if (!foreign) return false;

  logger.info('[amazonAds] Clearing the marketing cache: it belongs to a different user.');
  await clearStore(DB_OPTIONS, STORE_NAME);
  return true;
}

/**
 * Delete every cached marketing response.
 *
 * Registered in `utils/clearAppCaches.ts` alongside the SPI equivalent. The
 * blanket `indexedDB.databases()` sweep there already removes this database on
 * any browser that supports enumeration; this is the defensive path for the ones
 * that do not (Safari < 14, some webviews), where the sweep silently does
 * nothing.
 */
export async function clearMarketingCache(): Promise<void> {
  await clearStore(DB_OPTIONS, STORE_NAME);
}
